<template>
  <fullscreen-overlay-frame :title="title"
                            icon="room_preferences"
                            :color="color"
                            centered
                            closable
                            @close="close">

    <template v-slot:content>
      <v-card>
        <v-form v-model="valid">
          <v-list class="px-0">
            <!-- device name -->
            <v-list-item class="list-item straight">
              <v-list-item-content v-if="editName">
                <v-text-field outlined dense persistent-hint
                              ref="device-name-input"
                              :rules="nameRules"
                              :hint="$t('add-room-dialog.room-name')"
                              v-model="name">
                  <template v-slot:append>
                    <v-btn icon small
                           :loading="loading.changeName"
                           :disabled="!valid"
                           @click="editName = false">
                      <v-icon>check</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-list-item-content>
              <v-list-item-content v-else>
                <v-list-item-title v-text="name"/>
                <v-list-item-subtitle class="font-size-02"
                                      v-html="$t('add-room-dialog.room-name')"/>
              </v-list-item-content>
              <v-list-item-action v-if="!editName">
                <v-btn icon
                       @click="editName = true">
                  <v-icon class="non-flip">edit</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <content-card class="mb-4"
                :title="$t('device-list.title').toString()"
                  icon="widgets">
          <template v-slot:content>

          <!-- device list -->
          <v-list>
            <div v-if="loading.devices" class="text-center">
              <v-progress-circular color="primary" indeterminate/>
            </div>
            <v-list-item-group v-model="selectedDevices"
                               v-else
                               multiple
                               color="primary">
              <v-list-item v-for="device in deviceList" v-bind:key="device.id"
                           :value="device.encryptedId">
                <template v-slot:default="{ active }">
                  <v-list-item-icon class="mr-2">
                    <device-icon :device-type="device?.type" :is-colorized="isOnline(device)"/>
                  </v-list-item-icon>
                  <v-list-item-content class="device-title">
                    <v-list-item-title :class="isOnline(device) ? 'primary--text font-weight-bold' :''"
                                       class="font-size-03">
                      {{ device.name ?? $t('device-list.unsupported-device') }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="font-size-02"
                                          v-text="device.typeLabel"/>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-checkbox :input-value="active"
                                color="primary"/>
                  </v-list-item-action>
                </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          </template>
          </content-card>

          <content-card class="mb-4"
                :title="$t('add-rule-step-action-type.scenes').toString()"
                icon="play_circle">
          <template v-slot:content>

          <!-- scenes list -->
          <v-list>
            <div v-if="loading.scenes" class="text-center">
              <v-progress-circular color="primary" indeterminate/>
            </div>
            <v-list-item-group v-model="selectedScenes"
                               v-else
                               multiple
                               color="primary">
              <v-list-item v-for="scene in scenesList" v-bind:key="scene.id"
                           :value="scene.id">
                <template v-slot:default="{ active, toggle }">
                  <v-list-item-icon class="mr-2">
                    <v-icon left
                class="material-icons-outlined"
                color="primary">
                  {{ scene.iconKey }}
                </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="device-title">
                    <v-list-item-title class="primary--text font-weight-bold font-size-03">
                      {{ scene.name  }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="font-size-02">
                      <span>{{ $t("scenes-card.subtitle.actions-count", {n: scene.actions}) }} </span>
                      <span v-if="scene.delay">{{ $t("scenes-card.subtitle.delay", {delay: scene.delay}) }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-checkbox :input-value="active"
                                @click.stop="toggle" 
                                color="primary"/>
                    </v-list-item-action>
                  </template>
              </v-list-item>
            </v-list-item-group>
          </v-list>

          </template>
          </content-card>
        </v-form>
      </v-card>
    </template>

    <template v-slot:actions>
      <v-btn depressed large
             color="primary"
             :loading="loading.saving"
             :disabled="loading.saving || !valid"
             class="font-weight-bold action-button"
             v-text="$t('app.save')"
             @click="save">
      </v-btn>

      <v-btn v-if="data?.id"
             large depressed outlined
             color="primary"
             class="mt-4 ml-0 action-button"
             @click="deleteRoom">
        {{ $t('add-room-dialog.delete') }}
      </v-btn>
    </template>
  </fullscreen-overlay-frame>
</template>

<script>

import FullscreenOverlayFrame from "@/templates/dialogs/FullscreenOverlayFrame.vue";
import DeviceIcon from "@/templates/components/devices/DeviceIcon.vue";
import ContentCard from "@/templates/components/ContentCard"

export default {
  name: 'AddRoomDialog',
  components: {
    DeviceIcon,
    FullscreenOverlayFrame,
    ContentCard
  },
  props: ['data'],

  data: function () {
    return {
      loading: {
        room: false,
        devices: false,
        changeName: false,
        saving: false,
        scenes: false
      },
      valid: false,
      title: this.data?.id ? this.$t('add-room-dialog.update.title') : this.$t('add-room-dialog.title'),
      color: 'primary',
      deviceList: null,
      selectedDevices: this.data?.devices ?? [],
      name: this.data?.name ?? this.$t('add-room-dialog.default-name'),
      editName: !this.data?.id,
      scenesList: null,
      selectedScenes: [],
    }
  },

  computed: {
    nameRules() {
      return [
        v => !!v || this.$t('app.rules.required'),
        v => (!v || v.length <= 50) || this.$t('app.rules.too-long', {maxLength: 50})
      ]
    },
  },

  methods: {
    /**
     * init
     */
    init() {
      this.loading.devices = true
      this.loading.scenes = true
      this.getDevices()
          .then((devices) => {
            this.deviceList = devices
            this.loading.devices = false
          })

      this.getGroupScenes()
          .then((scenes) => {
            this.scenesList = scenes
            scenes.forEach(scene => {
              if (scene.selected) this.selectedScenes.push(scene.id)
            })
            this.loading.scenes = false
          })
    },

    /**
     * get devices from gateway
     * @returns {Promise<unknown>}
     */
    getDevices() {
      return new Promise((resolve, reject) => {
        this.$rhRequest.sendGet(
            {
              endpoint: 'devices/get',
            },
            (response) => {
              if (response.data?.data) {
                let devices = Object.values(response.data.data)

                // sort selected devices to top
                let selected = [], unselected = []
                devices.forEach(device => {
                  if (['controller'].includes(device.type)) {
                    // continue
                  } else if (this.data.devices?.includes(device.encryptedId)) {
                    selected.push(device)
                  } else {
                    unselected.push(device)
                  }
                })

                resolve(selected.concat(unselected))
              } else {
                reject(new Error('invalid response body'))
              }
            },
            (err) => {
              reject(err)
            }
        )
      })
    },

    getGroupScenes() {
      return new Promise((resolve, reject) => {
        this.$rhRequest.sendGet(
            {
              endpoint: `group/scenes/available-scenes?group_id=${this.data.id}`,
            },
            (response) => {
              if (response.data?.data) {
                let data = Object.values(response.data.data)
                let selected = [], unselected = []
                data.forEach(scene => {
                  if (scene.selected) {
                    selected.push(scene)
                  } else {
                    unselected.push(scene)
                  }
                })
                resolve(selected.concat(unselected))
              } else {
                reject(new Error('invalid response body'))
              }
            },
            (err) => {
              reject(err)
            }
        )
      })
    },

    /**
     * returns true if the device is online
     *
     * @param device
     * @returns {boolean}
     */
    isOnline(device) {
      return device.online && device.gatewayOnline
    },

    saveScenes(group_id) {
      let data = {
        group_id: group_id,
        scene_ids: this.selectedScenes
      }

      let endpoint = 'group/scenes/update-scenes'

      this.loading.saving = true
      this.$rhRequest.sendPost(
          {
            endpoint: endpoint,
            data: data,
          },
          () => {
            this.loading.saving = false

            this.$root.bisatoast.success({message: this.$t('add-room-dialog.save.success'), showCloseBtn: true})
            this.close()
          },
          (err) => {
            console.error(err)
            this.loading.saving = false
            this.$root.bisatoast.error({message: this.$t('app.generic-error'), showCloseBtn: true})
          }
      )
    },

    save() {
      let model = {
        name: this.name,
        devices: this.selectedDevices
      }

      let endpoint = 'group/add'
      let params
      if (this.data?.id) {
        model['id'] = this.data.id
        endpoint = 'group/update'
        params = {group_id: this.data.id}
      }

      this.loading.saving = true
      this.$rhRequest.sendPost(
          {
            endpoint: endpoint,
            data: model,
            params: params
          },
          (response) => {
            this.loading.saving = false

            let group_id
            if (this.data?.id) {
              group_id = this.data?.id
            } else if (response?.data?.data?.id) {
              group_id = response?.data?.data?.id
            }

            this.saveScenes(group_id)
          },
          (err) => {
            console.error(err)
            this.loading.saving = false
            this.$root.bisatoast.error({message: this.$t('app.generic-error'), showCloseBtn: true})
          }
      )
    },

    deleteRoom() {
      this.$root.bisadialog.toggle('confirmation', true, {
        maxWidth: 400,
        title: this.$t('confirmation-dialog.title'),
        text: this.$t('add-room-dialog.delete-room.confirm', {name: this.name}),
        confirmLabel: this.$t('add-room-dialog.delete')
      })
      this.$root.$on('dialogConfirmed', () => {
        this.$rhRequest.sendDelete({
          endpoint: 'group/delete',
          params: {group_id: this.data.id}
        }, () => {
          this.$root.bisatoast.success({
            message: this.$t('add-room-dialog.delete-room.success', {name: this.name}),
            showCloseBtn: true
          })
          this.close()
        }, (error) => {
          console.error(error)
          this.$root.bisatoast.error({message: this.$t('app.generic-error'), showCloseBtn: true})
        })
        this.$root.$off('dialogConfirmed')
      })
    },

    /**
     * close dialog
     */
    close() {
      this.$root.$emit('updateRoomList')
      this.$root.bisadialog.toggle('addRoom')
    }
  }
};
</script>

